import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  BookOpen, Video, FileText, Search, Play, Clock, Star, 
  Download, ExternalLink, ChevronRight, RefreshCw, Filter,
  Heart, Pill, Activity, Brain, Home, Users, Utensils, Moon
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Resource categories
const CATEGORIES = [
  { id: 'symptom_management', name: 'Symptom Management', icon: Activity, color: 'teal' },
  { id: 'medication', name: 'Medication Guides', icon: Pill, color: 'blue' },
  { id: 'comfort_care', name: 'Comfort Care', icon: Heart, color: 'rose' },
  { id: 'nutrition', name: 'Nutrition & Hydration', icon: Utensils, color: 'amber' },
  { id: 'emotional', name: 'Emotional Support', icon: Brain, color: 'purple' },
  { id: 'grief', name: 'Grief & Bereavement', icon: Heart, color: 'slate' },
  { id: 'caregiver_wellness', name: 'Caregiver Wellness', icon: Users, color: 'green' },
  { id: 'sleep', name: 'Sleep & Rest', icon: Moon, color: 'indigo' },
  { id: 'home_safety', name: 'Home Safety', icon: Home, color: 'orange' }
];

// Sample resources (in production, this would come from the API)
const SAMPLE_RESOURCES = [
  {
    id: '1',
    title: 'Managing Pain at End of Life',
    category: 'symptom_management',
    type: 'video',
    duration: '5:32',
    description: 'Learn how to recognize pain signs and administer comfort medications safely.',
    rating: 4.8,
    views: 1245
  },
  {
    id: '2',
    title: 'Safe Medication Administration',
    category: 'medication',
    type: 'guide',
    pages: 12,
    description: 'Step-by-step guide for giving medications including morphine, lorazepam, and more.',
    rating: 4.9,
    views: 2103
  },
  {
    id: '3',
    title: 'Providing Mouth Care',
    category: 'comfort_care',
    type: 'video',
    duration: '3:15',
    description: 'How to keep your loved one comfortable with proper oral care techniques.',
    rating: 4.7,
    views: 876
  },
  {
    id: '4',
    title: 'When Eating Becomes Difficult',
    category: 'nutrition',
    type: 'article',
    readTime: '8 min',
    description: 'Understanding changes in appetite and how to provide comfort without forcing food.',
    rating: 4.6,
    views: 1567
  },
  {
    id: '5',
    title: 'Caregiver Self-Care Essentials',
    category: 'caregiver_wellness',
    type: 'guide',
    pages: 8,
    description: 'Taking care of yourself while caring for others - practical tips and resources.',
    rating: 4.9,
    views: 3421
  },
  {
    id: '6',
    title: 'Recognizing Signs of Transition',
    category: 'symptom_management',
    type: 'video',
    duration: '7:45',
    description: 'Understanding the natural signs that indicate end of life is approaching.',
    rating: 4.8,
    views: 2876
  },
  {
    id: '7',
    title: 'Managing Anxiety and Agitation',
    category: 'emotional',
    type: 'article',
    readTime: '6 min',
    description: 'Techniques to calm and comfort your loved one during moments of distress.',
    rating: 4.7,
    views: 1234
  },
  {
    id: '8',
    title: 'Creating a Peaceful Environment',
    category: 'comfort_care',
    type: 'guide',
    pages: 6,
    description: 'Setting up the room for comfort, including lighting, music, and aromatherapy.',
    rating: 4.5,
    views: 987
  },
  // Grief & Bereavement Resources
  {
    id: '9',
    title: 'Understanding the Grief Journey',
    category: 'grief',
    type: 'guide',
    pages: 15,
    description: 'A compassionate guide to understanding the stages of grief and what to expect.',
    rating: 4.9,
    views: 4521
  },
  {
    id: '10',
    title: 'Anticipatory Grief: Grieving Before Loss',
    category: 'grief',
    type: 'video',
    duration: '12:30',
    description: 'Understanding and coping with the grief that begins before a loved one passes.',
    rating: 4.8,
    views: 3287
  },
  {
    id: '11',
    title: 'Supporting Children Through Grief',
    category: 'grief',
    type: 'guide',
    pages: 20,
    description: 'Age-appropriate ways to help children understand death and process their feelings.',
    rating: 4.9,
    views: 2876
  },
  {
    id: '12',
    title: 'Self-Care During Bereavement',
    category: 'grief',
    type: 'article',
    readTime: '10 min',
    description: 'Practical strategies for taking care of yourself while grieving.',
    rating: 4.7,
    views: 3654
  },
  {
    id: '13',
    title: 'Finding Meaning After Loss',
    category: 'grief',
    type: 'video',
    duration: '18:45',
    description: 'Stories and guidance on finding purpose and healing after losing a loved one.',
    rating: 4.9,
    views: 5123
  },
  {
    id: '14',
    title: 'Grief Support Groups: What to Expect',
    category: 'grief',
    type: 'article',
    readTime: '7 min',
    description: 'How grief support groups work and how they can help your healing journey.',
    rating: 4.6,
    views: 1987
  },
  {
    id: '15',
    title: 'Memorial Planning Guide',
    category: 'grief',
    type: 'guide',
    pages: 12,
    description: 'A step-by-step guide for planning meaningful memorials and celebrations of life.',
    rating: 4.8,
    views: 2345
  },
  {
    id: '16',
    title: 'Coping with Holidays After Loss',
    category: 'grief',
    type: 'video',
    duration: '9:15',
    description: 'Strategies for navigating holidays, birthdays, and anniversaries after a loss.',
    rating: 4.8,
    views: 4123
  }
];

const ResourceLibrary = () => {
  const [resources, setResources] = useState(SAMPLE_RESOURCES);
  const [filteredResources, setFilteredResources] = useState(SAMPLE_RESOURCES);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  // Fetch resources from API
  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/phase45/resources/library`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.resources?.length > 0) {
          setResources(data.resources);
          setFilteredResources(data.resources);
        }
      }
    } catch (err) {
      console.error('Error fetching resources:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Filter resources
  useEffect(() => {
    let filtered = resources;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.type === selectedType);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query)
      );
    }

    setFilteredResources(filtered);
  }, [resources, selectedCategory, selectedType, searchQuery]);

  // Log resource view
  const viewResource = async (resource) => {
    setSelectedResource(resource);
    try {
      await fetch(`${API}/api/phase45/resources/${resource.id}/view`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error logging view:', err);
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'guide': return <FileText className="h-4 w-4" />;
      case 'article': return <BookOpen className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  // Get category info
  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-teal-600" />
            Resource Library
          </h1>
          <p className="text-gray-600">Guides, videos, and articles to support your caregiving journey</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('all')}
          className={selectedCategory === 'all' ? 'bg-teal-600' : ''}
        >
          All Categories
        </Button>
        {CATEGORIES.map(category => {
          const Icon = category.icon;
          return (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className={selectedCategory === category.id ? 'bg-teal-600' : ''}
            >
              <Icon className="h-4 w-4 mr-1" />
              {category.name}
            </Button>
          );
        })}
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600">Type:</span>
        <div className="flex gap-2">
          {['all', 'video', 'guide', 'article'].map(type => (
            <Button
              key={type}
              variant={selectedType === type ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedType(type)}
            >
              {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
            </Button>
          ))}
        </div>
        <Badge variant="outline" className="ml-auto">
          {filteredResources.length} resources
        </Badge>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResources.map(resource => {
          const categoryInfo = getCategoryInfo(resource.category);
          const CategoryIcon = categoryInfo.icon;
          
          return (
            <Card 
              key={resource.id}
              className="cursor-pointer hover:border-teal-300 transition-colors group"
              onClick={() => viewResource(resource)}
            >
              <CardContent className="p-4">
                {/* Type Badge */}
                <div className="flex items-center justify-between mb-3">
                  <Badge 
                    variant="outline" 
                    className="flex items-center gap-1"
                  >
                    {getTypeIcon(resource.type)}
                    {resource.type}
                  </Badge>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="text-sm font-medium">{resource.rating}</span>
                  </div>
                </div>

                {/* Thumbnail/Icon */}
                <div className={`h-32 rounded-lg mb-3 flex items-center justify-center ${
                  categoryInfo.color === 'teal' ? 'bg-teal-100' :
                  categoryInfo.color === 'blue' ? 'bg-blue-100' :
                  categoryInfo.color === 'rose' ? 'bg-rose-100' :
                  categoryInfo.color === 'amber' ? 'bg-amber-100' :
                  categoryInfo.color === 'purple' ? 'bg-purple-100' :
                  categoryInfo.color === 'green' ? 'bg-green-100' :
                  categoryInfo.color === 'indigo' ? 'bg-indigo-100' : 'bg-orange-100'
                }`}>
                  {resource.type === 'video' ? (
                    <div className="relative">
                      <CategoryIcon className={`h-12 w-12 ${
                        categoryInfo.color === 'teal' ? 'text-teal-600' :
                        categoryInfo.color === 'blue' ? 'text-blue-600' :
                        categoryInfo.color === 'rose' ? 'text-rose-600' :
                        categoryInfo.color === 'amber' ? 'text-amber-600' :
                        categoryInfo.color === 'purple' ? 'text-purple-600' :
                        categoryInfo.color === 'green' ? 'text-green-600' :
                        categoryInfo.color === 'indigo' ? 'text-indigo-600' : 'text-orange-600'
                      }`} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="h-6 w-6 text-teal-600" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <CategoryIcon className={`h-12 w-12 ${
                      categoryInfo.color === 'teal' ? 'text-teal-600' :
                      categoryInfo.color === 'blue' ? 'text-blue-600' :
                      categoryInfo.color === 'rose' ? 'text-rose-600' :
                      categoryInfo.color === 'amber' ? 'text-amber-600' :
                      categoryInfo.color === 'purple' ? 'text-purple-600' :
                      categoryInfo.color === 'green' ? 'text-green-600' :
                      categoryInfo.color === 'indigo' ? 'text-indigo-600' : 'text-orange-600'
                    }`} />
                  )}
                </div>

                {/* Content */}
                <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-teal-600 transition-colors">
                  {resource.title}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {resource.description}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {resource.duration || resource.readTime || `${resource.pages} pages`}
                  </div>
                  <span>{resource.views?.toLocaleString()} views</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredResources.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-semibold text-gray-600">No Resources Found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters or search query</p>
          </CardContent>
        </Card>
      )}

      {/* Resource Detail Modal */}
      {selectedResource && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <Badge variant="outline" className="mb-2">
                    {getTypeIcon(selectedResource.type)}
                    <span className="ml-1">{selectedResource.type}</span>
                  </Badge>
                  <CardTitle className="text-xl">{selectedResource.title}</CardTitle>
                  <CardDescription>{getCategoryInfo(selectedResource.category).name}</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedResource(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Video/Image Placeholder */}
              {selectedResource.type === 'video' && (
                <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                  <Button size="lg" className="bg-white/20 hover:bg-white/30">
                    <Play className="h-8 w-8 text-white mr-2" />
                    <span className="text-white">Play Video</span>
                  </Button>
                </div>
              )}

              <p className="text-gray-700">{selectedResource.description}</p>

              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-amber-500 fill-current" />
                  <span>{selectedResource.rating} rating</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{selectedResource.duration || selectedResource.readTime || `${selectedResource.pages} pages`}</span>
                </div>
                <span>{selectedResource.views?.toLocaleString()} views</span>
              </div>

              <div className="flex gap-2">
                {selectedResource.type !== 'video' && (
                  <Button variant="outline" className="flex-1">
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                <Button className="flex-1 bg-teal-600 hover:bg-teal-700">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {selectedResource.type === 'video' ? 'Watch Full Video' : 'Read Now'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ResourceLibrary;
